import ShadowContainerHorizontal from '@atoms/ShadowContainerHorizontal/ShadowContainerHorizontal';
import Tag from '@atoms/Tag/Tag';
import { ICrossSellTabbedProductsList } from '@models/ICrossSellTabbedProductsList';
import BreakpointEnum from '@models/enums/BreakpointEnum';
import CrossSellProductCTAStatusEnum from '@models/enums/CrossSellProductCTAStatusEnum';
import TagTypesEnum from '@models/enums/TagTypesEnum';
import CrossSellProductCard from '@molecules/CrossSellProductCard/CrossSellProductCard';
import NavigationCarousel from '@molecules/NavigationCarousel/NavigationCarousel';
import ProductCard from '@molecules/ProductCard/ProductCard';
import updateDataLayer from '@utils/helpers/gtm';
import slugify from '@utils/helpers/slugify';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import useTabbedProductsCTAStatuses from '@utils/hooks/useTabbedProductsCTAStatuses';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { SwiperSlide } from 'swiper/react';
import { SwiperOptions } from 'swiper/types';

interface Props {
  readonly additionalBreakpoints?: { [width: number]: SwiperOptions };
  readonly backgroundColour?: string;
  readonly cardClassName?: string;
  readonly carouselHorizontalSpacingClassName?: string;
  readonly defaultTabType?: TagTypesEnum;
  readonly headerHorizontalSpacingClassName?: string;
  readonly isLightMode?: boolean;
  readonly itemsPayload: ICrossSellTabbedProductsList;
  readonly renderProductCard?: boolean;
  readonly shadowColour?: string;
  readonly shouldRestoreTab?: boolean;
  readonly titleClassName?: string;
  readonly trackingContentType?: string;
  readonly useShortCardOnMobile?: boolean;
  readonly onAddItemToCart: (
    onSuccessfulAddToCart: (
      tabbedProductsList: ICrossSellTabbedProductsList
    ) => void
  ) => (code: string) => void;
  readonly onRemoveItemFromCart?: (
    onSuccessfulRemoveFromCart: () => void
  ) => (code: string) => void;
}

const TabbedCrossSellProductListing = ({
  itemsPayload: { title, addToBasketLabel, addedLabel, removeLabel, tabs },
  isLightMode,
  backgroundColour = 'bg-white',
  shadowColour = 'from-white',
  titleClassName,
  cardClassName,
  defaultTabType = TagTypesEnum.Three,
  carouselHorizontalSpacingClassName = '',
  headerHorizontalSpacingClassName = '',
  additionalBreakpoints = {},
  renderProductCard = false,
  shouldRestoreTab = true,
  trackingContentType,
  useShortCardOnMobile = false,
  onAddItemToCart,
  onRemoveItemFromCart,
}: Props) => {
  const isMobile = useMediaQuery(768);
  const isSmallMobile = useMediaQuery(376);
  const isInitialRender = useRef(true);
  const firstSlideRef = useRef<HTMLDivElement>(null);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [recentlyAddedProducts, setRecentlyAddedProducts] = useState<
    Set<string>
  >(new Set());
  const tabsTitles = tabs?.map((tab) => tab.title) || [];

  useEffect(() => {
    // reset the active tab index when new tab data is passed to the component to prevent no tab being selected
    if (!tabs?.[activeTabIndex]?.items) setActiveTabIndex(0);
  }, [activeTabIndex, tabs]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentional Additional Dependencies
  useEffect(() => {
    // We want to avoid focusing the first slide on mount as it will break the focus
    // when this component is used on a regular page (e.g. Basket page)
    if (firstSlideRef.current && !isInitialRender.current) {
      firstSlideRef.current.focus({ preventScroll: true });
    }

    if (isInitialRender.current) isInitialRender.current = false;
  }, [activeTabIndex]);

  // Stores the added sku in temporary state, so we can show the "Added" label for 2 seconds.
  const showAddedStatus = (sku: string) => {
    setRecentlyAddedProducts((currentProducts) => {
      const nextProducts = new Set(currentProducts);
      nextProducts.add(sku);
      return nextProducts;
    });

    // Clear after 2 seconds, when the "Added" label disappears.
    setTimeout(() => {
      setRecentlyAddedProducts((currentProducts) => {
        const nextProducts = new Set(currentProducts);
        nextProducts.delete(sku);
        return nextProducts;
      });
    }, 2000);
  };

  const { itemsCTAStatuses, handleUpdateItemCTAStatus } =
    useTabbedProductsCTAStatuses(tabs || []);

  const handleSuccessfulAddItemToCart =
    (sku: string) => (tabbedProductsList: ICrossSellTabbedProductsList) => {
      // Fallback to first tab if the items at activeIndexTab is undefined
      // This is only required on the basket page when removing items, we need to fall back at least the first tab
      if (
        tabbedProductsList?.tabs &&
        tabs &&
        tabs.length !== tabbedProductsList.tabs.length &&
        shouldRestoreTab
      ) {
        setActiveTabIndex(0);
      }

      showAddedStatus(sku);
    };

  const handleSuccessfulRemoveItemFromCart = (index: number) => () =>
    handleUpdateItemCTAStatus(
      activeTabIndex,
      index,
      CrossSellProductCTAStatusEnum.AddToBasket
    );

  const tabsContainer = (
    <div
      className="min-w-0"
      data-testid="Tabbed-Cross-Sell-Product-Listing-Tabs"
    >
      <ShadowContainerHorizontal
        threshold={0.01}
        shadowColour={shadowColour}
        hasTabbableNestedItems
      >
        <div className="flex gap-x-5">
          {tabsTitles.map((tabTitle, index) => {
            const isActive = index === activeTabIndex;

            return (
              <Tag
                key={tabTitle}
                text={tabTitle}
                tagType={isActive ? TagTypesEnum.Secondary : defaultTabType}
                pointerStates={!isActive}
                disabled={isActive}
                onClick={() => {
                  setActiveTabIndex(index);

                  updateDataLayer({
                    event: 'selectContent',
                    content_type: trackingContentType,
                    link_text: tabTitle,
                  });
                }}
                isLightMode={isLightMode}
              />
            );
          })}
        </div>
      </ShadowContainerHorizontal>
    </div>
  );

  return (
    <section
      className={classNames('text-black-100', backgroundColour)}
      data-testid="Tabbed-Cross-Sell-Product-Listing"
    >
      {title && (
        <p
          className={classNames('mb-5 md:mb-7.5', titleClassName)}
          data-testid="Tabbed-Cross-Sell-Product-Listing-Title"
        >
          {title}
        </p>
      )}

      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{
            opacity: 0,
            transition: {
              duration: 0,
            },
          }}
          key={`${tabsTitles[activeTabIndex]}-${activeTabIndex}`}
          data-testid="Tabbed-Cross-Sell-Product-Listing-Carousel-Wrapper"
        >
          <NavigationCarousel
            id="cross-sell-product-carousel"
            items={tabs?.[activeTabIndex]?.items || []}
            loop={false}
            shadows
            shadowColour={shadowColour}
            slidesToShowTablet={3}
            headerWrapperClassName="gap-x-5 md:gap-x-10 md:min-h-[2.5rem]"
            headerSlot={tabsContainer}
            headerHorizontalSpacingClassName={headerHorizontalSpacingClassName}
            carouselHorizontalSpacingClassName={
              carouselHorizontalSpacingClassName
            }
            isSoftLoadAnimationDisabled
            desktopBreakpoint={BreakpointEnum.Medium}
            spaceBetween={isMobile ? 20 : 16}
            shouldReinitializeNavigation
            enableDomObserver
            additionalBreakpoints={{
              375: {
                slidesPerView: 1.3,
              },
              480: {
                slidesPerView: 1.5,
              },
              565: {
                slidesPerView: 2.1,
              },
              ...additionalBreakpoints,
            }}
            isLightMode={isLightMode}
          >
            {(itemsToDisplay) =>
              itemsToDisplay.map((item, index) => (
                <SwiperSlide
                  key={slugify(`${item.name}-${index}`)}
                  className="stretch-card"
                >
                  <div
                    className="h-full"
                    tabIndex={-1}
                    ref={index === 0 ? firstSlideRef : null}
                  >
                    {renderProductCard ? (
                      <ProductCard
                        {...item}
                        addToBasketLabel={addToBasketLabel}
                        onAddToCart={onAddItemToCart(
                          handleSuccessfulAddItemToCart(item.sku!)
                        )}
                        productNameClasses={classNames(
                          'max-sm:pb-2.5',
                          isSmallMobile ? 'h6' : ''
                        )}
                        classes="max-sm:py-5"
                        useShortCardOnMobile={useShortCardOnMobile}
                      />
                    ) : (
                      <CrossSellProductCard
                        {...item}
                        ctaStatus={
                          recentlyAddedProducts.has(item.sku ?? '')
                            ? CrossSellProductCTAStatusEnum.Added
                            : (itemsCTAStatuses[activeTabIndex]?.[index] ??
                              CrossSellProductCTAStatusEnum.AddToBasket)
                        }
                        addToBasketLabel={addToBasketLabel}
                        addedLabel={addedLabel}
                        removeLabel={removeLabel}
                        onAddToCart={onAddItemToCart(
                          handleSuccessfulAddItemToCart(item.sku!)
                        )}
                        onRemoveFromCart={onRemoveItemFromCart!(
                          handleSuccessfulRemoveItemFromCart(index)
                        )}
                        isLightMode={isLightMode}
                        dataTestId={`Cross-Sell-Product-Card-Tab-${activeTabIndex}-Item-${index}`}
                        className={cardClassName}
                      />
                    )}
                  </div>
                </SwiperSlide>
              ))
            }
          </NavigationCarousel>
        </motion.div>
      </AnimatePresence>
    </section>
  );
};

export default TabbedCrossSellProductListing;
